import React, {useEffect, useState} from "react";
import type { ContextProps } from '../../../../types';
import type { AphLoanItem } from './aphLoan.types';
import {isNullOrUndefined} from "../../../../Utilities/commonUtilities";
import {aphLoanApi} from "./aphLoanApi";
import {SYSTEM_TYPES} from "../../../Library/libraryConstants";
import {getSystemTypeTextByValue} from "../../../Library/libraryUtility";
import {PageTitle} from "../../../../Components/Landmarks";
import {AphLoanSearchFormView} from "./AphLoanSearchFormView";
import {LayoutFullWidth} from "../../../../Components/Layout";
import {AphLoanTable} from "./AphLoanTable";
import {allow, policyEvents} from "../../../../Components/Authorize";
import {aphLoanItemModel, updateAphLoanItemModel} from "./aphLoanFactory";
import {GridCell, gridConstants, GridContainer} from "../../../../Components/Grid";
import {Button, ButtonLinkStyle} from "../../../../Components/Inputs";
import {getChangedAphLoanItemsForUpdate} from "./aphLoanUtility";
import {isArrayNullOrEmpty} from "../../../../Utilities/Types/arrayUtilities";

export const AphLoanManagementContainer = ({
                                               aphLoanSearchCriteria,
                                               handleApiCall,
                                               handleSetAphLoanSearchCriteria,
                                               isLoading
                                           }: ContextProps) => {

    const [aphLoanItems, setAphLoanItems] = useState<AphLoanItem[]>([]);
    const [originalAphLoanItems, setOriginalAphLoanItems] = useState<AphLoanItem[]>([]);

    const [aphLoanSearchDropDowns, setAphLoanSearchDropDowns] = useState<Record<string, unknown> | null>(null);

    const canManageAphLoans = allow(policyEvents.MANAGE_APH_LOANS);

    const loadAphLoanItemsResult = (result: Record<string, unknown>[]) => {
        const aphLoanItems = result.map((loan: Record<string, unknown>) => aphLoanItemModel(loan));
        setAphLoanItems(aphLoanItems);
        setOriginalAphLoanItems(aphLoanItems);
    };

    const handleGetAphLoanItemsReadyForProcessing = () => {
        handleApiCall(() => aphLoanApi.getAphItemsReadyForProcessing({
            ...aphLoanSearchCriteria,
        }), loadAphLoanItemsResult);
    }

    const handleGetAphLoanSearchDropDowns = () => {
        handleApiCall(() => aphLoanApi.getDropdownListsForAphLoanSearch(), (results: Record<string, unknown>) => setAphLoanSearchDropDowns(results));
    }


    const updateAphLoanItemInState = (aphLoanItemId: string, aphLoanStatus: string | number, notes: string | null) => {

        setAphLoanItems((currentAphLoanItems) =>
            currentAphLoanItems.map((item: AphLoanItem) =>
                item.aphLoanItemId === aphLoanItemId
                    ? {
                        ...item,
                        aphLoanStatus,
                        aphLoanNotes: notes ? notes : item.aphLoanNotes,
                    }
                    : item
            )
        );
    };

    const syncOriginalAphLoanItemAfterSave = (updatedAphLoanItem: AphLoanItem) => {

        setOriginalAphLoanItems((currentAphLoanItems) =>
            currentAphLoanItems.map((item: AphLoanItem) =>
                item.aphLoanItemId === updatedAphLoanItem.aphLoanItemId
                    ? {
                        ...item,
                        aphLoanStatus: updatedAphLoanItem.aphLoanStatus,
                        aphLoanNotes: updatedAphLoanItem.aphLoanNotes ? updatedAphLoanItem.aphLoanNotes : item.aphLoanNotes,
                    }
                    : item
            )
        );
    };

    const handleSaveUpdatedAphLoanItem = (updatedAphLoanItem: AphLoanItem) => {
        updateAphLoanItemInState(updatedAphLoanItem.aphLoanItemId, updatedAphLoanItem.aphLoanStatus, updatedAphLoanItem.aphLoanNotes);

        const aphLoanItemForSaving = [
            updateAphLoanItemModel({
                aphLoanItemId: updatedAphLoanItem.aphLoanItemId,
                aphLoanStatus: updatedAphLoanItem.aphLoanStatus,
                aphLoanNotes: updatedAphLoanItem.aphLoanNotes,
            })
        ];

        handleApiCall(() => aphLoanApi.updateAphLoans(aphLoanItemForSaving), syncOriginalAphLoanItemAfterSave);
    }

    const handleSaveUpdatedAphLoanItems = () => {

        const updatedAphLoanItems = getChangedAphLoanItemsForUpdate(aphLoanItems, originalAphLoanItems);

        if (updatedAphLoanItems.length === 0) {
            alert("No changes to save.");
            return;
        }

        if (confirm(`Are you sure you want to save ${updatedAphLoanItems.length} updated APH loan item(s)?`))
            handleApiCall(() => aphLoanApi.updateAphLoans(updatedAphLoanItems), handleGetAphLoanItemsReadyForProcessing);
    };

    const handleExportLoansClick = () => {
        if (isArrayNullOrEmpty(aphLoanItems)) {
            alert("Search criteria has no results. Adjust your search and try again.")
        } else {
            handleApiCall(() => aphLoanApi.getAphLoansReport(aphLoanSearchCriteria));
        }
    };


    useEffect(() => {
        handleGetAphLoanItemsReadyForProcessing();
    }, [aphLoanSearchCriteria]);


    useEffect(() => {
        handleGetAphLoanSearchDropDowns();
    }, []);

    if (isNullOrUndefined(aphLoanSearchDropDowns)) return;

    return <>
        <PageTitle h1={`${getSystemTypeTextByValue(SYSTEM_TYPES.APH.toString())} Loan Management`}/>

        <AphLoanSearchFormView
            aphLoanSearchCriteria={aphLoanSearchCriteria}
            aphLoanSearchDropDowns={aphLoanSearchDropDowns}
            handleSetAphLoanSearchCriteria={handleSetAphLoanSearchCriteria}
            isLoading={isLoading}
        />

        <LayoutFullWidth className={`catalog`}>
            <header>
                <h2>Search Results</h2>
            </header>
            {
                aphLoanItems &&
                <>
                    <GridContainer isReverseOrder className={`form__header`} hasMarginX>
                        <GridCell
                            medium_columns={gridConstants.column.THREE}
                            small_columns={gridConstants.column.SIX}>
                            <Button
                                disabled={isLoading}
                                isPrimary
                                label="Save Updates"
                                name="btnSave"
                                onClick={handleSaveUpdatedAphLoanItems}
                            />
                        </GridCell>
                        <GridCell
                            medium_columns={gridConstants.column.ONE}>
                            <ButtonLinkStyle
                                isCollapsed
                                label={`Export APH Loans`}
                                name="btnExportLoans"
                                onClick={handleExportLoansClick}
                            />
                        </GridCell>
                    </GridContainer>

                    <AphLoanTable
                        aphLoanItems={aphLoanItems}
                        aphLoanSearchCriteria={aphLoanSearchCriteria}
                        canManageAphLoans={canManageAphLoans}
                        handleSetAphLoanSearchCriteria={handleSetAphLoanSearchCriteria}
                        handleSaveUpdatedAphLoanItem={handleSaveUpdatedAphLoanItem}
                        updateAphLoanItemInState={updateAphLoanItemInState}
                    />
                </>
            }
        </LayoutFullWidth>
    </>;
};

